import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Button, Space, Card, Select, message, Spin, Alert, Row, Col } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import useAuth from '../../hooks/useAuth';
import { useTemplate } from '../../hooks/useManufacturing';
import { useValidateResourceCompany } from '../../hooks/useValidateResourceCompany';
import { useItems } from '../../hooks/useInventory';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { createId } from '../../utils';

const { TextArea } = Input;
const { Option } = Select;

const TemplateForm: React.FC = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { uid } = useParams<{ uid?: string }>();
  const { config } = useConfig();
  const { company } = useCompany();
  const { validateResourceCompany, isSwitchingCompany } = useValidateResourceCompany();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [loading, setLoading] = useState(false);

  // Fetch template data if we are editing
  const { data: templateData, isLoading: isTemplateLoading, error: templateError } = useTemplate(tokenData, { template_uid: uid || undefined, company_uid: company.current?.uid });
  
  // Fetch products/materials from inventory to build the option lists
  const { data: inventoryData, isLoading: isInventoryLoading } = useItems(tokenData, { company: company.current?.uid, module: config.module });
  const { securePost } = useSecureRequest();

  const fetchedElements = React.useRef<Record<number, any>>({});

  const handleElementSelect = async (elementId: number) => {
    if (!elementId || (fetchedElements.current[elementId] && !fetchedElements.current[elementId]._loading)) return;
    
    const inventoryItem = inventoryData?.itemList?.find((i: any) => i.id === elementId);
    if (!inventoryItem?.uid) return;

    fetchedElements.current[elementId] = { _loading: true }; 
    const hide = message.loading(`Obteniendo detalles de ${inventoryItem.name}...`, 0);
    try {
      const res = await securePost('/inventory/item/get-item', {
        company: company.current?.uid,
        uidItem: inventoryItem.uid
      });
      if (res?.inventoryItem) {
        fetchedElements.current[elementId] = res.inventoryItem;
        hide();
      } else {
        delete fetchedElements.current[elementId];
        hide();
        message.warning(`No se pudo obtener el detalle completo de ${inventoryItem.name}`);
      }
    } catch (e: any) {
      delete fetchedElements.current[elementId];
      hide();
      message.error(`Error al consultar ${inventoryItem.name}: ${e.message || 'Fallo de conexión'}`);
    }
  };

  const isEditMode = !!uid;

  useEffect(() => {
    if (templateError) {
      message.error(templateError.message || "Error al cargar la plantilla");
      history.push('/templates');
    }
    if (templateData?.message) {
      message.error(templateData.message);
      history.push('/templates');
    }
  }, [templateError, templateData, history]);

  // Validación de empresa: la plantilla debe pertenecer a la empresa activa
  useEffect(() => {
    if (isEditMode && templateData?.template) {
      validateResourceCompany(
        templateData.template.company_id,
        templateData.template.company_uid || templateData.template.companies?.uid,
        '/templates',
        'plantilla de producción'
      );
    }
  }, [isEditMode, templateData?.template, validateResourceCompany]);

  useEffect(() => {
    if (isEditMode && templateData?.template) {
      const template = templateData.template;
      form.setFieldsValue({
        manufacturing_type: template.manufacturing_type || 'DISCRETE',
        name: template.name,
        description: template.description,
        final_element_id: template.final_element_id,
        target_quantity: template.target_quantity,
        is_active: template.is_active === undefined ? true : template.is_active,
        inputs: (template.inputs || template.production_template_inputs || []).map((item: any) => ({
          element_id: item.element_id,
          quantity: item.quantity
        })),
        outputs: (template.outputs || template.production_template_outputs || []).map((out: any) => ({
          element_id: out.element_id,
          expected_quantity: out.expected_quantity
        }))
      });

      // Pre-fetch elements for existing template or use Firestore data
      if (template.inputs && template.inputs.length > 0) {
        template.inputs.forEach((i: any) => {
          if (i.element_data) fetchedElements.current[i.element_id] = i.element_data;
        });
      } else {
        (template.inputs || template.production_template_inputs || []).forEach((i: any) => handleElementSelect(i.element_id));
      }

      if (template.manufacturing_type === 'DISCRETE' && template.final_element_id) {
        handleElementSelect(template.final_element_id);
      }

      if (template.outputs && template.outputs.length > 0) {
        template.outputs.forEach((o: any) => {
          if (o.element_data) fetchedElements.current[o.element_id] = o.element_data;
        });
      } else {
        (template.outputs || template.production_template_outputs || []).forEach((o: any) => handleElementSelect(o.element_id));
      }
    }
  }, [templateData, form, isEditMode, inventoryData]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const fetchFullElementData = async (list: any[]) => {
        if (!list) return [];
        return await Promise.all(list.map(async (item: any) => {
          if (!item?.element_id) return item;

          if (fetchedElements.current[item.element_id] && !fetchedElements.current[item.element_id]._loading) {
            return { ...item, element_data: fetchedElements.current[item.element_id] };
          }

          const inventoryItem = inventoryItems.find((i: any) => i.id === item.element_id);
          if (inventoryItem?.uid) {
            const res = await securePost('/inventory/item/get-item', {
              company: company.current?.uid,
              uidItem: inventoryItem.uid
            });
            if (res?.inventoryItem) {
              fetchedElements.current[item.element_id] = res.inventoryItem;
              return { ...item, element_data: res.inventoryItem };
            }
          }
          return item;
        }));
      };

      const populatedInputs = await fetchFullElementData(values.inputs);
      
      let rawOutputs = values.outputs || [];
      if (values.manufacturing_type === 'DISCRETE' && values.final_element_id) {
        rawOutputs = [{
          element_id: values.final_element_id,
          expected_quantity: values.target_quantity || 1
        }];
      }
      
      const populatedOutputs = await fetchFullElementData(rawOutputs);

      const payload = {
        company: company.current?.uid,
        template: {
          ...values,
          company_id: company.current?.id,
          inputs: populatedInputs,
          outputs: populatedOutputs
        }
      };

      if (isEditMode) {
        payload.template.id = templateData?.template?.id;
        const res = await securePost('/manufacturing/update-template', {
          ...payload,
          csrfToken: tokenData?.token?.uid
        });
        if(res && !res.error) {
          message.success('Plantilla actualizada con éxito');
          history.push('/templates');
        } else {
          message.error(res?.message || res?.error || 'Error al actualizar la plantilla');
        }
      } else {
        payload.template.document_number = `TMPL-${createId(8)}`;
        const res = await securePost('/manufacturing/create-template', {
          ...payload,
          csrfToken: tokenData?.token?.uid
        });
        if(res && !res.error) {
          message.success('Plantilla creada con éxito');
          history.push('/templates');
        } else {
          message.error(res?.message || res?.error || 'Error al crear la plantilla');
        }
      }
    } catch (error: any) {
      message.error(error.message || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const inventoryItems = inventoryData?.itemList || []; // useItems returns itemList
  const getElementName = (elementId: number) => {
    return inventoryItems.find((item: any) => item.id === elementId)?.name || `Desconocido (${elementId})`;
  };
  const elementOptions = inventoryItems.map((item: any) => (
    <Option key={item.id} value={item.id}>{item.name}</Option>
  ));

  const hasCompanyMismatch = Boolean(
    isEditMode &&
    templateData?.template?.company_id &&
    company.current?.id &&
    company.current.id !== 0 &&
    templateData.template.company_id !== company.current.id
  );

  if ((isEditMode && isTemplateLoading) || isInventoryLoading || isSwitchingCompany || hasCompanyMismatch) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}><Spin size="large" tip={isSwitchingCompany || hasCompanyMismatch ? "Validando empresa de la plantilla..." : undefined} /></div>;
  }

  return (
    <Card title={isEditMode ? `Editar Plantilla de Producción ${templateData?.template?.document_number}` : "Crear Plantilla de Producción"} style={{ margin: '24px' }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ inputs: [{}], outputs: [{}], manufacturing_type: 'DISCRETE' }}
      >
        <Form.Item name="manufacturing_type" label="Tipo de Manufactura" rules={[{ required: true }]}>
          <Select disabled={isEditMode}>
            <Option value="DISCRETE">Discreta (1 Producto Final)</Option>
            <Option value="PROCESS">Por Procesos (Múltiples Salidas/Mermas)</Option>
          </Select>
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="name" label="Nombre de la Plantilla" rules={[{ required: true, message: 'Requerido' }]}>
              <Input placeholder="Ej. Lote Pan de Queso" />
            </Form.Item>
          </Col>
          <Form.Item noStyle dependencies={['manufacturing_type']}>
            {() => {
              const mfgType = form.getFieldValue('manufacturing_type');
              if (mfgType === 'DISCRETE') {
                return (
                  <Col span={12}>
                    <Form.Item name="final_element_id" label="Producto Final Esperado" rules={[{ required: true, message: 'Requerido' }]}>
                      <Select placeholder="Selecciona el producto a fabricar" showSearch optionFilterProp="children" onChange={handleElementSelect}>
                        {elementOptions}
                      </Select>
                    </Form.Item>
                  </Col>
                );
              }
              return null;
            }}
          </Form.Item>
        </Row>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="target_quantity" label="Cantidad Base a Producir" rules={[{ required: true, message: 'Requerido' }]}>
              <InputNumber min={1} style={{ width: '100%' }} placeholder="Ej. 100" />
            </Form.Item>
          </Col>
          {isEditMode && (
            <Col span={12}>
               <Form.Item name="is_active" label="Estado">
                 <Select>
                   <Option value={true}>Activo</Option>
                   <Option value={false}>Inactivo</Option>
                 </Select>
               </Form.Item>
            </Col>
          )}
        </Row>

        <Form.Item name="description" label="Descripción / Notas" rules={[{ required: false }]}>
          <TextArea rows={3} placeholder="Instrucciones generales de elaboración..." />
        </Form.Item>

        <div style={{ marginTop: '24px', marginBottom: '16px' }}>
          <h4>Materiales Requeridos (BOM)</h4>
          <Alert message="Añade los insumos y materias primas necesarios para fabricar la cantidad base especificada arriba." type="info" showIcon style={{ marginBottom: '16px' }} />
        </div>

        <Form.List name="inputs">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={[name, 'element_id']}
                    rules={[{ required: true, message: 'Insumo faltante' }]}
                    style={{ width: '300px' }}
                  >
                    <Select placeholder="Seleccionar insumo" showSearch optionFilterProp="children" onChange={handleElementSelect}>
                      {elementOptions}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'quantity']}
                    rules={[{ required: true, message: 'Cantidad requerida' }]}
                  >
                    <InputNumber placeholder="Cantidad" min={0.01} step={0.01} style={{ width: '150px' }} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Añadir Insumo
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item noStyle dependencies={['manufacturing_type', 'final_element_id', 'target_quantity']}>
          {() => {
            const mfgType = form.getFieldValue('manufacturing_type');
            if (mfgType === 'PROCESS') {
              return (
                <>
                  <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                    <h4>Productos Resultantes Esperados (Outputs)</h4>
                    <Alert message="Añade los productos que se generarán al fabricar la cantidad base especificada arriba." type="info" showIcon style={{ marginBottom: '16px' }} />
                  </div>
                  <Form.List name="outputs">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                            <Form.Item
                              {...restField}
                              name={[name, 'element_id']}
                              rules={[{ required: true, message: 'Producto faltante' }]}
                              style={{ width: '300px' }}
                            >
                              <Select placeholder="Seleccionar producto final" showSearch optionFilterProp="children" onChange={handleElementSelect}>
                                {elementOptions}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, 'expected_quantity']}
                              rules={[{ required: true, message: 'Cantidad requerida' }]}
                            >
                              <InputNumber placeholder="Cantidad" min={0.01} step={0.01} style={{ width: '150px' }} />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />
                          </Space>
                        ))}
                        <Form.Item>
                          <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                            Añadir Producto Resultante
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </>
              );
            } else if (mfgType === 'DISCRETE') {
              const finalElementId = form.getFieldValue('final_element_id');
              const targetQty = form.getFieldValue('target_quantity') || 1;
              if (!finalElementId) return null;

              return (
                <>
                  <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                    <h4>Producto Resultante Esperado (Output)</h4>
                    <Alert message="Este es el producto final que se generará al completar la orden. Está configurado en la sección superior." type="success" showIcon style={{ marginBottom: '16px' }} />
                  </div>
                  <Space style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item style={{ width: '300px' }}>
                      <Input value={getElementName(finalElementId)} disabled />
                    </Form.Item>
                    <Form.Item>
                      <InputNumber value={targetQty} disabled style={{ width: '150px' }} />
                    </Form.Item>
                  </Space>
                </>
              );
            }
            return null;
          }}
        </Form.Item>

        <div style={{ position: 'sticky', bottom: 0, background: '#fff', padding: '16px', textAlign: 'right', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', zIndex: 10, margin: '24px -24px -24px -24px', borderRadius: '0 0 8px 8px' }}>
          <Space>
            <Button onClick={() => history.push('/templates')}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Guardar Plantilla
            </Button>
          </Space>
        </div>
      </Form>
    </Card>
  );
};

export default TemplateForm;
